import { EventApi } from "@fullcalendar/core";
import { useQueryClient } from "@tanstack/react-query";
import { AppointmentContext } from "../../stores/Appointment/appointment.provider";
import useStore from "../../helpers/useStore";
import { AppointmentDto, TherapyDto, WorkingHoursDto } from "../../api-client";
import { CalendarEvent } from "./controller/adapters";
import { useApi } from "../../api/ApiProvider";
import { userKeys } from "../../stores/User/user.keys";
import moment from "moment";
import { useHorizon } from "./useHorizon";

/**
 * Check if a time slot falls outside the employee's working hours
 * @param appointmentStart - Time slot start time
 * @param appointmentEnd - Time slot end time
 * @param workingHours - Array of working hour blocks for the employee on that day (excluding breaks)
 * @returns true if time slot is outside working hours
 */
  
const useCalendarValidations = () => {

  const { frequencyValidationForAppointment } = useStore(AppointmentContext);
  const queryClient = useQueryClient();
  const { user: userApi } = useApi();
  const { horizonDate, isPastHorizon } = useHorizon();

  const frequencyValidation = async (appointmentId: string, date: string): Promise<any> => {
      const result = await frequencyValidationForAppointment(appointmentId, date);
      // console.log('frequencyValidationForAppointment:', result);
      
      return result;
    }

  // Check if the event is overlapping with other events in a day for a patient with different therapists
  const isPatientEventOverlapping = (start: Date, end: Date, events: EventApi[], draggedEvent: EventApi): boolean => {
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    
    const patientTodayEvents = events.filter((e) => 
      e.extendedProps.timeSlotType === "Treatment" &&
      e.extendedProps.patient?.id === parseInt(draggedEvent.extendedProps.patient?.id)
    );

    for (let i = 0; i < patientTodayEvents.length; i++) {
      let event = patientTodayEvents[i];
      if (event.id === draggedEvent.id) { //|| event.start?.getDate() !== start.getDate()
        continue;
      }
      
      if (draggedEvent.extendedProps.multipleTimeSlots) {
        const groupped = events.filter((e) => e.groupId === draggedEvent.groupId);
        if (groupped.some((g) => g.id === event.id))
          continue;
      }
      
      const eventStart = new Date(event.startStr).getTime();
      const eventEnd = new Date(event.endStr).getTime();
      
      if ((startTime < eventEnd && endTime > eventStart)) {
        return true;
      }
    }
    return false;
  };

  const isPatientAppointmentOverlapping = (start: Date, end: Date, appointments: CalendarEvent[], appointment: AppointmentDto): boolean => {
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();
    
    const patientTodayAppointments = appointments.filter((app) => 
      app.timeSlots?.[0].type === "TimeSlot" &&
      app.timeSlots?.[0].patient?.id === appointment.timeSlots?.[0].patient?.id
    );

    for (let i = 0; i < patientTodayAppointments.length; i++) {
      let app = patientTodayAppointments[i];
      if (app.id === appointment.id) {
        continue;
      }

      const appStart = new Date(app.start!).getTime();
      const appEnd = new Date(app.end!).getTime();
      
      if ((startTime < appEnd && endTime > appStart)) {
        return true;
      }
    }
    return false;
  }

  // Validates against the specific, tarif-bound therapy id (e.g. "GKV_MT", "Privat_MT"),
  // NOT the shared display abbreviation ("MT"). A therapist with only "Privat_MT" must not
  // pass validation for a "GKV_MT" appointment even though both display as "MT".
  const hasTherapistCredentials = async (userId: string, therapyId: string | string[]): Promise<boolean> => {
    try {
      const user = await queryClient.fetchQuery({
        queryKey: userKeys.userByIdWithTherapies.detail(userId),
        queryFn: () => userApi.getUserByIdWithTherapies(userId),
        staleTime: 60 * 60 * 1000,
      });

      if (user) {
        const therapyIds = (Array.isArray(therapyId) ? therapyId : [therapyId]).filter(Boolean);
        if (therapyIds.length === 0) return false;
        return therapyIds.every((id) =>
          (user.therapies as TherapyDto[])?.some((ut) => ut.id === id)
        );
      }
      return false;
    } catch (error) {
      console.error("Error fetching user credentials:", error);
      return false;
    }
  };

  /**
   * A same-day pairing is a deliberate Doppelbehandlung when the moved/placed
   * appointment and an existing one carry a slot on the SAME therapyRx and
   * that line allows more than one unit per day (`unitsPerDay > 1`) — e.g. the
   * BG "2 Behandlungseinheiten pro Behandlungstag" shape as two attendances.
   * The duplicate-day prompt must not fire on those.
   */
  const isDoppelPairing = (
    movedTimeSlots: any[] | undefined,
    event: CalendarEvent
  ): boolean => {
    const doubledIds = new Set(
      (movedTimeSlots ?? [])
        .filter((ts: any) => (ts?.therapyRx?.unitsPerDay ?? 1) > 1)
        .map((ts: any) => ts.therapyRx.id)
    );
    if (doubledIds.size === 0) return false;
    const eventSlots: any[] = (event.extendedProps as any)?.timeSlots ?? [];
    return eventSlots.some((ts: any) => ts?.therapyRx?.id && doubledIds.has(ts.therapyRx.id));
  };

  /**
   * Checks if a patient already has appointments on the given date across all therapists.
   * Uses the already-loaded calendarEvents (which span all visible users) instead of an API call.
   * Returns the list of other matching CalendarEvents found (excluding the given appointmentId).
   */
  const hasPatientAppointmentOnDate = (
    patientId: number | string,
    date: Date,
    calendarEvents: CalendarEvent[],
    currentAppointmentId?: string
  ): CalendarEvent[] => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    const pid = typeof patientId === "string" ? parseInt(patientId, 10) : patientId;

    return calendarEvents.filter((event) => {
      // Only check actual appointments (not working hours, availability, etc.)
      if (event.extendedProps?.eventType !== "appointment") return false;

      // Match patient
      const eventPatientId = event.extendedProps?.patient?.id;
      if (!eventPatientId || eventPatientId !== pid) return false;

      // Exclude the appointment being moved
      const eventAppointmentId = event.extendedProps?.appointmentId || event.id;
      if (eventAppointmentId && String(eventAppointmentId) === String(currentAppointmentId)) return false;

      // Check same date
      if (!event.start) return false;
      return moment(event.start).format("YYYY-MM-DD") === dateStr;
    });
  };

  const isAppointmentOutsideWorkingHours = (
  appointmentStart: Date,
  appointmentEnd: Date,
  workingHours: WorkingHoursDto[]
): boolean => {
  const workBlocks = workingHours.filter(wh => !wh.isBreak);

  // If no work blocks defined, assume no restrictions (don't show warning)
  if (workBlocks.length === 0) {
    return false;
  }

  // Check if appointment overlaps with any work block
  const isInsideAnyBlock = workBlocks.some(block => {
    const blockStart = new Date(block.start);
    const blockEnd = new Date(block.end);
    
    // Appointment is inside if both start and end are within the block
    return appointmentStart >= blockStart && appointmentEnd <= blockEnd;
  });

  // If not inside any block, it's outside working hours
  return !isInsideAnyBlock;
};

  // Time-only comparison for Dauertermin: work plan hours are projected to horizon week date,
  // but appointments use the selected date — so we only compare hours:minutes
  const isTimeOutsideContinuousWorkingHours = (
    appointmentStart: Date,
    appointmentEnd: Date,
    workingHours: WorkingHoursDto[]
  ): boolean => {
    const workBlocks = workingHours.filter(wh => !wh.isBreak);
    if (workBlocks.length === 0) return false;

    const apptStartMin = appointmentStart.getHours() * 60 + appointmentStart.getMinutes();
    const apptEndMin = appointmentEnd.getHours() * 60 + appointmentEnd.getMinutes();

    const isInsideAnyBlock = workBlocks.some(block => {
      const blockStart = new Date(block.start);
      const blockEnd = new Date(block.end);
      const blockStartMin = blockStart.getHours() * 60 + blockStart.getMinutes();
      const blockEndMin = blockEnd.getHours() * 60 + blockEnd.getMinutes();
      return apptStartMin >= blockStartMin && apptEndMin <= blockEndMin;
    });

    return !isInsideAnyBlock;
  };

  return {
    frequencyValidation,
    hasTherapistCredentials,
    isPatientEventOverlapping,
    isPatientAppointmentOverlapping,
    hasPatientAppointmentOnDate,
    isDoppelPairing,
    isAppointmentOutsideWorkingHours,
    isTimeOutsideContinuousWorkingHours,
    horizonDate,
    isPastHorizon
  };

};

export default useCalendarValidations;